// istanbul ignore file
import React, { useEffect } from 'react'
import BackButton from '../../../../components/BackButton'
import {
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  Tooltip
} from '@mui/material'
import { type Option } from '../../../../components/GenericDropdown'
import { CautionWarning } from './Warnings'
import { useNavigate } from 'react-router-dom'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { Button } from '../../../../components/Button'
import Modal from '../../../../components/Modal'
import LanguageStrings from '../../../../i18n/locales'
import { GenXDropdown } from '../../../../components/GenXDropdown'
import CreateKpiTab from './CreateKpiTab'
import { type AssetDeviceState } from '../../redux/assetListSlice'
import { type FrequencyMetaDataState } from '../../../device-management/redux/frequencyMetaDataSlice'
import {
  createCustomKpiAction,
  fetchAssetDeviceList,
  fetchSiteList,
  addAssetKpiAlarm,
  editCustomKpiDetails
} from '../../redux/actionCreators'
import {
  fetchAssetCategories,
  fetchDeviceType,
  fetchDeviceModel,
  fetchFrequencyMetaData
} from '../../../device-management/redux/actionCreators'
import { type CreateCustomKpiRequest } from '../../services/createCustomKpiService'
import { type CreateCustomKpiState } from '../../redux/createCustomKpiSlice'
import { type AssetAlarmState } from '../../redux/addAssetKpiAlarmSlice'
import { useSelector, useDispatch } from 'react-redux'
import { reset, setTotalizer } from '../../redux/customKpiSlice'
import { type AssetCategoriesState } from '../../../device-management/redux/assetCategoriesSlice'
import { type DeviceTypeState } from '../../../device-management/redux/deviceTypeSlice'
import { type DeviceModelState } from '../../../device-management/redux/deviceModelSlice'
import { type SiteListState } from '../../redux/siteListSlice'
import HandleAPIFailures from '../HandleAPIFailures'
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import useCleanup from '../useCleanup'
import Switch, { type SwitchProps } from '@mui/material/Switch'
import { styled } from '@mui/material/styles'
import CustomToast from '../ToastComponent'
import './../ConfigureAlarm.css'
import { assetAlarmReset } from '../../redux/addAssetKpiAlarmSlice'
import { createKpiReset } from '../../redux/createCustomKpiSlice'
import { updateKpiReset } from '../../redux/editCustomKpiSlice'
import { type EditKpiState } from '../../redux/editCustomKpiSlice'
import {
  isValidKpiData,
  hasSpecialCharAtStartOrEnd,
  sanitizeInput
} from '../../../../utils/validator'
import { useAuth } from 'react-oidc-context'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ConfigureDevice from '../../../device-management/components/configureDevice'
import {
  DEFAULT_FREQUENCY_VALUE,
  MAX_KPI_NAME_LENGTH,
  MAX_KPI_REMARK_LENGTH
} from '../../../../utils/KpiConstants'

const KPIDetailStrings = LanguageStrings().ThermalIQStrings.KpiDetailsComponent
const CreateCustomKPIStrings =
  LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent

export interface Indicator {
  kpiDefinitionId: string
  kpiName: string
  assetId: string | null
  isKpiEnabled: boolean
  isAlarmApplicable: boolean
  isAlarmEnabled: boolean
  isCustomKpi: boolean
  tenantId: string | null
  unit: string | null
  isDefaultConfig: boolean
  executionFrequencyMinutes: number | null
  isTotalizer?: boolean
  totalizerType?: string | null
  alarmLowerLimit: number | null
  alarmLowerLimitStateText: string | null
  alarmLowerLimitTextColorId: string | null
  alarmLowerLimitColorName: string | null
  alarmLowerLimitColorValue: string | null
  alarmLowerLimitSeverityId: string | null
  alarmLowerLimitSeverityLevel: string | null
  alarmLowerLimitSeverity: string | null
  alarmLowerLimitLabelId: string | null
  alarmLowerLimitLabelName: string | null
  alarmLowerLimitRecommendationText: string
  alarmHigherLimit: string | null
  alarmHigherLimitStateText: string
  alarmHigherLimitTextColorId: string | null
  alarmHigherLimitColorName: string | null
  alarmHigherLimitColorValue: string | null
  alarmHigherLimitSeverityId: string | null
  alarmHigherLimitSeverityLevel: string | null
  alarmHigherLimitSeverity: string | null
  alarmHigherLimitLabelId: string | null
  alarmHigherLimitLabelName: string | null
  alarmHigherLimitRecommendationText: string
}

const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '5px 9px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#64C3FF',
        opacity: 1,
        border: 0
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      },
      '& .MuiSwitch-thumb': {
        color: '#000'
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#707070'
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 15,
    height: 15
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#B0B0B0',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}))

interface LabelValue {
  label: string
  value: string
  typeId?: string
}

const CreateCustomKpi = (props: PropsFromRedux): JSX.Element => {
  useCleanup()
  const [kpiType, setKpiType] = React.useState<Option | null>(null)
  const [deviceType, setDeviceType] = React.useState<Option | null>(null)
  const [deviceModel, setDeviceModel] = React.useState<Option | null>(null)
  const [site, setSite] = React.useState<Option | null>(null)
  const [asset, setAsset] = React.useState<LabelValue | null>(null)
  const [showConfirmation, setShowConfirmation] = React.useState(false)
  const [showCreatePopup, setShowCreatePopup] = React.useState(false)
  const [showApiFailurePopup, setShowApiFailurePopup] = React.useState(false)
  const [name, setName] = React.useState('')
  const [remarks, setRemarks] = React.useState('')
  const [frequency, setFrequency] = React.useState(DEFAULT_FREQUENCY_VALUE)
  const [unit, setUnit] = React.useState('')
  const [dashboardText, setDashboardText] = React.useState('')
  const [error, setError] = React.useState<boolean>(false)
  const [nameErrorInit, setNameErrorInit] = React.useState(false)
  const [remarksErrorInit, setRemarksErrorInit] = React.useState(false)
  const [unitErrorInit, setUnitErrorInit] = React.useState(false)
  const [dashboardTextErrorInit, setDashboardTextErrorInit] =
    React.useState(false)
  const customKpiState = useSelector((state: RootState) => state.customKpi)
  const frequencyMetaDataState = useSelector((state: RootState) => state.frequencyMetaDataState)
  const [categoryTypeId, setCategoryTypeId] = React.useState('')
  const [assetOptions, setAssetOptions] = React.useState<LabelValue[]>([])
  const [deviceTypeId, setDeviceTypeId] = React.useState('')
  const [enabled, setEnabled] = React.useState(false)
  const [isTotalizer, setIsTotalizer] = React.useState(customKpiState.totalizer)
  const thermalCategory = 'Thermal'
  const [isAlarm, setIsAlarm] = React.useState(false)
  const [assetIndicators, setAssetIndicators] = React.useState<any>([])
  const [kpiId, setKpiId] = React.useState('')
  const [kpiCreated, setKpiCreated] = React.useState(false)
  const [showAlarmPopup, setShowAlarmPopup] = React.useState(false)

  // Create frequency options from API or use default values
  const frequencyOptions = React.useMemo(() => {
    if (frequencyMetaDataState?.data?.kpiFrequencies != null && frequencyMetaDataState.data.kpiFrequencies.length > 0) {
      return frequencyMetaDataState.data.kpiFrequencies.map(option => ({
        label: option.label,
        value: option.value
      }))
    }
    return []
  }, [frequencyMetaDataState.data?.kpiFrequencies])

  const operationPermissions: OperationPermissionState = useSelector(
    (state: RootState) => state.operationPermissions
  )
  const auth = useAuth()

  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    setIsTotalizer(customKpiState.totalizer)
  }, [customKpiState.totalizer])

  useEffect(() => {
    !operationPermissions.updated &&
      props.getOperationPermissions(auth.user?.profile.sub as string)
    const indicatorArr = [
      {
        kpiDefinitionId: '999',
        kpiName: '',
        assetId: '',
        isKpiEnabled: true,
        isAlarmApplicable: true,
        isAlarmEnabled: false,
        isCustomKpi: true,
        tenantId: 'honeywell',
        unit: null,
        isDefaultConfig: false,
        executionFrequencyMinutes: null,
        isTotalizer: false,
        totalizerType: null,
        alarmLowerLimit: null,
        alarmLowerLimitStateText: null,
        alarmLowerLimitTextColorId: null,
        alarmLowerLimitColorName: null,
        alarmLowerLimitColorValue: null,
        alarmLowerLimitSeverityId: null,
        alarmLowerLimitSeverityLevel: null,
        alarmLowerLimitSeverity: null,
        alarmLowerLimitLabelId: null,
        alarmLowerLimitLabelName: null,
        alarmLowerLimitRecommendationText: null,
        alarmHigherLimit: null,
        alarmHigherLimitStateText: null,
        alarmHigherLimitTextColorId: null,
        alarmHigherLimitColorName: null,
        alarmHigherLimitColorValue: null,
        alarmHigherLimitSeverityId: null,
        alarmHigherLimitSeverityLevel: null,
        alarmHigherLimitSeverity: null,
        alarmHigherLimitLabelId: null,
        alarmHigherLimitLabelName: null,
        alarmHigherLimitRecommendationText: null
      }
    ]
    setAssetIndicators(indicatorArr)
    return () => {
      dispatch(assetAlarmReset())
      dispatch(createKpiReset())
      dispatch(updateKpiReset())
    }
  }, [])

  useEffect(() => {
    if (kpiType?.value === 'option1') {
      props.fetchSiteList()
      props.fetchFrequencyMetaData()
    }
  }, [kpiType?.value])

  useEffect(() => {
    if (site?.value != null) {
      props.fetchAssetDeviceList(site.value)
    }
  }, [site])

  useEffect(() => {
    if (site?.value != null && asset?.value != null) {
      props.fetchAssetDeviceList(site.value, asset.value)
    }
  }, [asset])

  useEffect(() => {
    if (kpiType?.value === 'option2') {
      props.fetchAssetCategories()
    }
  }, [kpiType?.value])

  useEffect(() => {
    if (
      props.assetCategories?.assetCategories != null &&
      props.assetCategories.assetCategories.length > 0
    ) {
      const thermalCat = props.assetCategories.assetCategories.find(
        (cat: any) => cat.category_type_name === thermalCategory
      )
      if (thermalCat != null) {
        setCategoryTypeId(thermalCat.category_type_id)
      }
    }
  }, [props.assetCategories?.assetCategories != null])

  useEffect(() => {
    if (deviceType?.value != null) {
      props.fetchDeviceModel(deviceType.value)
    }
  }, [deviceType])

  useEffect(() => {
    if (categoryTypeId !== '') {
      props.fetchDeviceType(categoryTypeId)
    }
  }, [categoryTypeId])

  useEffect(() => {
    if (
      !props.assetDeviceList.isLoading &&
      props.assetDeviceList !== null &&
      props.assetDeviceList.devices.length === 0
    ) {
      const assetOptions: LabelValue[] = props.assetDeviceList.assets.map(
        (asset) => ({
          label: asset.name,
          value: asset.id,
          typeId: asset.assetType
        })
      )
      setAssetOptions(assetOptions)
    }
  }, [props.assetDeviceList, site])

  const deviceTypeOptions: Option[] =
    props.deviceType.deviceType != null
      ? props.deviceType.deviceType.map((device) => ({
        label: device.asset_type_name,
        value: device.asset_type_id
      }))
      : []

  const deviceModelOptions: Option[] =
    props.deviceModel.deviceModel != null
      ? props.deviceModel.deviceModel.map((device) => ({
        label: device.assetModel,
        value: device.assetModelId
      }))
      : []

  const siteOptions: Option[] =
    props.siteList.sites != null
      ? props.siteList.sites.map((site) => ({
        label: site.siteName,
        value: site.id
      }))
      : []

  const handleClose = (): void => {
    setShowApiFailurePopup(false)
  }

  const handleChooseKpiType = (selectedOption: Option | null): void => {
    dispatch(reset())
    setKpiType(selectedOption)
    if (selectedOption !== null && selectedOption.value === 'option2') { setIsAlarm(false) }
    setShowApiFailurePopup(true)
  }

  const handleChooseDeviceType = (value: Option): void => {
    setDeviceType(value)
    setDeviceTypeId(value.value)
    setShowApiFailurePopup(false)
    if (deviceModel != null) {
      setDeviceModel(null)
    }
  }

  const handleChooseSite = (value: Option): void => {
    setSite(value)
    setAsset(null)
    setShowApiFailurePopup(true)
  }

  const handleChooseAsset = (value: Option): void => {
    setAsset(value)
    setShowApiFailurePopup(true)
  }

  const handleChooseDeviceModel = (value: Option): void => {
    setDeviceModel(value)
  }

  const checkForIncompleteForm = (): boolean => {
    return (
      kpiType === null ||
      isKpiNameNotValid() ||
      isKpiRemarkNotValid() ||
      (kpiType?.value === 'option1' &&
        !customKpiState.totalizer &&
        frequency === '') ||
      (kpiType?.value === 'option1' &&
        !customKpiState.totalizer &&
        isFrequencyNotValid()) ||
      error ||
      isDashboardTextNotValid() ||
      (isAlarm &&
        ((assetIndicators[0].alarmLowerLimit === null ||
          assetIndicators[0].alarmLowerLimit === '')) &&
          ((assetIndicators[0].alarmHigherLimit === null ||
           assetIndicators[0].alarmHigherLimit === ''))) ||
      (isAlarm &&
        ((assetIndicators[0].lowerLimitOperator === null ||
          assetIndicators[0].lowerLimitOperator === '')) &&
          ((assetIndicators[0].higherLimitOperator === null ||
           assetIndicators[0].higherLimitOperator === ''))) ||
      (!customKpiState.totalizer &&
        customKpiState.stages[0].arithmeticIndicator.value === '') ||
      (!customKpiState.totalizer &&
        customKpiState.stages[0].parameter1.parameter === '' &&
        customKpiState.stages[0].parameter1.constantValue === '') ||
      (!customKpiState.totalizer &&
        customKpiState.stages[0].parameter2.parameter === '' &&
        customKpiState.stages[0].parameter2.constantValue === '') ||
      (customKpiState.totalizer &&
        (customKpiState.stages[0].parameter2.parameter === '' ||
          isScaleNotValid() ||
          customKpiState.totalizerType === '' ||
          customKpiState.totalizePeriod === '')) ||
      (customKpiState.totalizer &&
        customKpiState.trueCondition &&
        (customKpiState.stages[0].parameter1.parameter === '' ||
          customKpiState.stages[0].parameter2.parameter === ''))
    )
  }

  // istanbul ignore next
  const onCustomKpiCreationConfirmClose = (): void => {
    // istanbul ignore next
    setShowConfirmation(false)
  }

  // Helper to convert dashboardText string to object
  const parseDashboardText = (input: string): Record<string, string> => {
    // Add curly braces if not present
    let jsonString = input.trim()
    if (!jsonString.startsWith('{')) {
      jsonString = `{${jsonString}}`
    }
    try {
      // Replace single quotes with double quotes if needed
      jsonString = jsonString.replace(/([a-zA-Z0-9_]+)\s*:/g, '"$1":')
      return JSON.parse(jsonString)
    } catch {
      return {}
    }
  }

  const createCustomKpi = (): void => {
    const createCustomKpiRequest = {
      kpiName: name,
      kpiDescription: remarks,
      isAssetLevel: kpiType?.value === 'option1' ? 1 : 0,
      higherLimitNotification: 'high',
      lowerLimitNotification: 'low',
      deviceType:
        kpiType?.value === 'option1' ? asset?.typeId : deviceType?.value,
      deviceModel: deviceModel?.value,
      units: unit.trim(),
      isAlarmEnabled: isAlarm,
      isEnabled: kpiType?.value === 'option1' ? enabled : null,
      frequency:
        customKpiState.totalizer || frequency === '' ? 1 : parseInt(frequency),
      scaleFactor: customKpiState.totalizer ? customKpiState.scaleFactor : null,
      totalizerType: customKpiState.totalizer
        ? customKpiState.totalizerType
        : null,
      totalizePeriod: customKpiState.totalizer
        ? customKpiState.totalizePeriod
        : null,
      dashboardText: parseDashboardText(dashboardText),
      stage: customKpiState.stages.reduce((acc, stage, index) => {
        if (
          stage.parameter1.parameter === '' &&
          stage.parameter1.constantValue === '' &&
          stage.parameter2.parameter === '' &&
          stage.parameter2.constantValue === ''
        ) {
          return acc
        }
        const kpiParameters = []
        // For index 0, include both parameter1 and parameter2
        if (index === 0) {
          if (
            !customKpiState.trueCondition ||
            stage.parameter1.parameter !== '' ||
            stage.parameter1.constantValue !== ''
          ) {
            kpiParameters.push({
              siteId: site?.value,
              assetId: asset?.value,
              deviceId: stage.parameter1.deviceId,
              parameterType:
                customKpiState.totalizer && !customKpiState.trueCondition
                  ? 'NO_CONDITION'
                  : stage.parameter1.parameterType,
              parameterId:
                customKpiState.totalizer && !customKpiState.trueCondition
                  ? ''
                  : stage.parameter1.parameterId,
              isDeviceId:
                kpiType?.value === 'option1' &&
                customKpiState.totalizer &&
                !customKpiState.trueCondition
                  ? false
                  : stage.parameter1.constantValue === '' ||
                    stage.parameter1.constantValue === undefined,
              constantValue: !customKpiState.totalizer
                ? stage.parameter1.constantValue
                : ''
            })
          }
          if (
            stage.parameter2.parameter !== '' ||
            stage.parameter2.constantValue !== ''
          ) {
            kpiParameters.push({
              siteId: site?.value,
              assetId: asset?.value,
              deviceId: stage.parameter2.deviceId,
              parameterType: stage.parameter2.parameterType,
              parameterId: stage.parameter2.parameterId,
              isDeviceId:
                stage.parameter2.constantValue === '' ||
                stage.parameter2.constantValue === undefined,
              constantValue: !customKpiState.totalizer
                ? stage.parameter2.constantValue
                : ''
            })
          }
        } else {
          if (
            !customKpiState.totalizer &&
            (stage.parameter2.parameter !== '' ||
              stage.parameter2.constantValue !== '')
          ) {
            kpiParameters.push({
              siteId: site?.value,
              assetId: asset?.value,
              deviceId: stage.parameter2.deviceId,
              parameterType: stage.parameter2.parameterType,
              parameterId: stage.parameter2.parameterId,
              isDeviceId:
                stage.parameter2.constantValue === '' ||
                stage.parameter2.constantValue === undefined,
              constantValue: stage.parameter2.constantValue
            })
          }
        }
        // If no parameters were added, ignore this stage
        if (kpiParameters.length === 0) {
          return acc
        }
        acc.push({
          stageId: index + 1,
          operator: customKpiState.totalizer
            ? 'TOTALIZER'
            : stage.arithmeticIndicator.value,
          operatorType: customKpiState.totalizer
            ? 'TOTALIZER'
            : stage.operatorType,
          calculatedName: stage.calculatedName,
          kpiParameters
        })
        return acc
      }, []) // Reduce to only valid stages
    }
    if (!kpiCreated) {
      props.createCustomKpiAction(createCustomKpiRequest)
    } else {
      props.editCustomKpiDetails(createCustomKpiRequest, kpiId)
    }
  }

  React.useEffect(() => {
    if (
      kpiType?.value === 'option1' &&
      props.createCustomKpi.data !== undefined &&
      props.createCustomKpi.data != null &&
      'kpi_definition_id' in props.createCustomKpi.data
    ) {
      const kpiId: string | null =
        (props.createCustomKpi?.data as any)?.kpi_definition_id ?? null
      if (typeof kpiId === 'string' && !kpiCreated && isAlarm) {
        setKpiCreated(true)
        setKpiId(kpiId)
        createAlarm(kpiId)
        setShowCreatePopup(false)
        setShowAlarmPopup(true)
      }
    }
  }, [props.createCustomKpi])

  React.useEffect(() => {
    if (
      kpiType?.value === 'option1' &&
      props.editCustomKpi.data !== undefined &&
      props.editCustomKpi.data != null &&
      kpiCreated &&
      isAlarm
    ) {
      createAlarm(kpiId)
      setShowCreatePopup(false)
      setShowAlarmPopup(true)
    }
  }, [props.editCustomKpi])

  const createAlarm = (newKpiId: string): void => {
    const updatedIndicatorArr = assetIndicators.map(
      (indicator: any, index: any) =>
        index === 0
          ? {
              ...indicator,
              kpiDefinitionId: newKpiId,
              kpiName: name,
              assetId: asset?.value,
              isKpiEnabled: kpiType?.value === 'option1' ? enabled : false,
              isAlarmEnabled: !!isAlarm,
              unit: unit.trim(),
              executionFrequencyMinutes:
                customKpiState.totalizer || frequency === ''
                  ? 1
                  : parseInt(frequency),
              isTotalizer: customKpiState.totalizer,
              totalizerType: customKpiState.totalizer
                ? customKpiState.totalizerType
                : null
            }
          : indicator
    )
    const indicatorPostData = {
      assetTypeId: asset?.typeId,
      assetKpiConfigs: updatedIndicatorArr
    }
    props.addAssetKpiAlarm(indicatorPostData, asset?.value ?? '')
  }

  const kpiTypeOptions = [
    { value: 'option1', label: 'Asset Level Custom KPI' },
    { value: 'option2', label: 'Device Level Custom KPI' }
  ]

  const textStyle = {
    root: {
      backgroundColor: '#272727',
      '& .MuiInputBase-input': {
        color: '#F0F0F0',
        backgroundColor: '#404040'
      },
      '& .MuiInputLabel-root': {
        color: '#F0F0F0',
        '&.Mui-focused': {
          color: '#F0F0F0'
        }
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#00000000'
        },
        '&:hover fieldset': {
          borderColor: '#00000000'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00000000'
        }
      }
    }
  }

  const kpiNameErrorMessage = (): string | null => {
    if (name.length > MAX_KPI_NAME_LENGTH) {
      return KPIDetailStrings.KpiNameLengthError
    } else if (hasSpecialCharAtStartOrEnd(name)) {
      return KPIDetailStrings.NotAllowedError
    } else if (!isValidKpiData(name)) {
      return KPIDetailStrings.KpiNameErrorMsg
    }
    return null
  }
  const kpiRemarkErrorMessage = (): string | null => {
    if (remarks?.length > MAX_KPI_REMARK_LENGTH) {
      return KPIDetailStrings.KpiRemarksLengthError
    } else if (hasSpecialCharAtStartOrEnd(remarks)) {
      return KPIDetailStrings.NotAllowedError
    } else if (!isValidKpiData(remarks)) {
      return KPIDetailStrings.KpiRemarkErrorMsg
    }
    return null
  }

  const isKpiNameNotValid = (): boolean => {
    return !isValidKpiData(name) || name.length > MAX_KPI_NAME_LENGTH
  }
  const isKpiRemarkNotValid = (): boolean => {
    return !isValidKpiData(remarks) || remarks.length > MAX_KPI_REMARK_LENGTH
  }
  const isValidFrequency = (value: string): boolean => {
    const regexValidator = /^(?!0$)\d{1,500}$/
    return regexValidator.test(value)
  }
  const isValidUnit = (value: string): boolean => {
    // Regex explanation:
    // ^[^\s].{0,28}[^\s]$  - Matches strings of length 2-30:
    //   ^           - Start of string
    //   [^\s]       - First character is not whitespace
    //   .{0,28}     - Followed by 0 to 28 of any character (so total length is 2-30)
    //   [^\s]       - Last character is not whitespace
    //   $           - End of string
    // |             - OR
    // ^[^\s]$       - Matches a single non-whitespace character (length 1)
    // This allows 1-30 characters, no leading/trailing whitespace, internal spaces allowed.
    const regexValidator = /^[^\s].{0,28}[^\s]$|^[^\s]$/
    return regexValidator.test(value)
  }
  const isFrequencyNotValid = (): boolean => {
    return !isValidFrequency(frequency)
  }
  const isValidScale = (value: string): boolean => {
    const regexValidator = /^(?:0|[1-9]\d*)(?:\.\d+)?$/
    return regexValidator.test(value)
  }
  const isScaleNotValid = (): boolean => {
    return !isValidScale(customKpiState.scaleFactor)
  }
  const isUnitNotValid = (): boolean => {
    if (unit.trim() === '') {
      return false // Empty unit is now valid (optional field)
    }
    return !isValidUnit(unit) || hasSpecialCharAtStartOrEnd(unit)
  }
  const isDashboardTextNotValid = (): boolean => {
    // Matches: "0": "Text", "-1": "Text", "1.5": "Text", ...
    const regexValidator = /^(\s*"([+-]?\d+(\.\d+)?)"\s*:\s*"(.*?)"\s*,?)*\s*$/
    const trimmed = dashboardText.trim()
    // Also check length and special char at start/end
    if (!regexValidator.test(trimmed) || hasSpecialCharAtStartOrEnd(trimmed)) {
      return true
    }
    // Try to parse as JSON after applying the same transformation as parseDashboardText
    let jsonString = trimmed
    if (!jsonString.startsWith('{')) {
      jsonString = `{${jsonString}}`
    }
    // Replace unquoted keys with quoted keys
    jsonString = jsonString.replace(/([a-zA-Z0-9_]+)\s*:/g, '"$1":')
    try {
      const parsed = JSON.parse(jsonString)
      // Check for any empty value (e.g., "23":"")
      for (const key in parsed) {
        if (typeof parsed[key] !== 'string' || parsed[key].trim() === '') {
          return true
        }
      }
      return false
    } catch {
      return true
    }
  }

  const breadcrumbStyle = {
    fontFamily: 'Honeywell Sans Web',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    textAlign: 'left',
    cursor: 'pointer'
  }

  const warningMessages = (): string[] => {
    let messages = []
    if (kpiType?.value === 'option1') {
      messages = [
        CreateCustomKPIStrings.YouHaveNotSelectedSite,
        CreateCustomKPIStrings.YouHaveNotSelectedAsset
      ]
    } else if (kpiType?.value === 'option2') {
      messages = [
        CreateCustomKPIStrings.YouHaveNotSelectedDeviceType,
        CreateCustomKPIStrings.YouHaveNotSelectedDeviceModel
      ]
    } else {
      messages = [CreateCustomKPIStrings.YouHaveNotSelectedKpiType]
    }
    return messages
  }

  const headingMessage = (): string => {
    let heading = ''
    if (kpiType?.value === 'option1') {
      heading = CreateCustomKPIStrings.SelectSiteAndAsset
    } else if (kpiType?.value === 'option2') {
      heading = CreateCustomKPIStrings.SelectDeviceTypeAndDeviceModel
    } else {
      heading = CreateCustomKPIStrings.SelectKpiType
    }
    return heading
  }

  const closeAndCleanState = (): void => {
    setShowCreatePopup(false)
  }
  const closeAndCleanAlarmState = (): void => {
    setShowAlarmPopup(false)
  }

  const handleTotalizerChange = (e: any): void => {
    setIsTotalizer(e.target.checked)
    dispatch(setTotalizer(e.target.checked))
  }

  const gridSizes = {
    xs: 12,
    sm: kpiType?.value === 'option1' ? 6 : 4,
    md: kpiType?.value === 'option1' ? 3 : 3,
    lg: kpiType?.value === 'option1' ? 3 : 3
  }

  const handleCreationWithFailure = (): JSX.Element => {
    return (
      <>
        <Card
          sx={{
            backgroundColor: '#272727',
            borderRadius: '10px',
            color: '#F0F0F0 !important',
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible'
          }}
        >
          <div style={{ marginLeft: '15px' }}></div>
          <Typography
            sx={{
              marginLeft: '15px',
              fontFamily: 'Honeywell Sans Web',
              fontSize: '16px',
              fontWeight: 600,
              letterSpacing: '2.5px'
            }}
          >
            {KPIDetailStrings.KPIInformation}
          </Typography>
          <CardContent>
            <Grid container spacing={3} alignItems='flex-start'>
              <Grid
                item
                xs={gridSizes.xs}
                sm={gridSizes.sm}
                md={gridSizes.md}
                lg={gridSizes.lg}
              >
                <GenXDropdown
                  data-testid='kpitype'
                  placeholder='Select an option'
                  value={kpiType}
                  options={kpiTypeOptions}
                  onSelect={handleChooseKpiType}
                  label={'KPI Type'}
                />
              </Grid>
              <Grid
                item
                xs={gridSizes.xs}
                sm={gridSizes.sm}
                md={gridSizes.md}
                lg={gridSizes.lg}
              >
                <Typography
                  sx={{
                    fontFamily: 'Honeywell Sans Web',
                    fontSize: '15px',
                    padding: 0.5,
                    display: 'block'
                  }}
                >
                  {KPIDetailStrings.KpiName}
                  <sup style={{ color: '#B0B0B0' }}> *</sup>
                </Typography>
                <TextField
                  id='name-textfield'
                  variant='outlined'
                  placeholder='Text'
                  InputLabelProps={{
                    disabled: true
                  }}
                  size='small'
                  className='custom-textfield'
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (e.target.value.length === 0) setNameErrorInit(false)
                    else setNameErrorInit(true)
                  }}
                  sx={{
                    ...textStyle.root,
                    width: '80%',
                    border:
                      isKpiNameNotValid() && nameErrorInit
                        ? '1px solid #EE3426'
                        : 'none'
                  }}
                />
                {isKpiNameNotValid() && nameErrorInit && (
                  <FormHelperText
                    sx={{ color: '#EE3426', paddingLeft: '2px' }}
                    className='errorColor'
                  >
                    {kpiNameErrorMessage()}
                  </FormHelperText>
                )}
              </Grid>
              <Grid
                item
                xs={gridSizes.xs}
                sm={gridSizes.sm}
                md={gridSizes.md}
                lg={gridSizes.lg}
              >
                <Typography
                  sx={{
                    fontFamily: 'Honeywell Sans Web',
                    fontSize: '15px',
                    padding: 0.5,
                    display: 'block'
                  }}
                >
                  {KPIDetailStrings.Remarks}
                  <sup style={{ color: '#B0B0B0' }}> *</sup>
                </Typography>
                <TextField
                  id='remarks-textfield'
                  variant='outlined'
                  placeholder='Text'
                  InputLabelProps={{
                    disabled: true
                  }}
                  size='small'
                  className='custom-textfield'
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value)
                    if (e.target.value.length === 0) setRemarksErrorInit(false)
                    else setRemarksErrorInit(true)
                  }}
                  sx={{
                    ...textStyle.root,
                    width: '80%',
                    border:
                      isKpiRemarkNotValid() && remarksErrorInit
                        ? '1px solid #EE3426'
                        : 'none'
                  }}
                />
                {isKpiRemarkNotValid() && remarksErrorInit && (
                  <FormHelperText
                    sx={{ color: '#EE3426', paddingLeft: '2px' }}
                    className='errorColor'
                  >
                    {kpiRemarkErrorMessage()}
                  </FormHelperText>
                )}
              </Grid>
              {(kpiType?.value === 'option1' ||
                kpiType?.value === 'option2') && (
                <Grid
                  item
                  xs={gridSizes.xs}
                  md={1.5}
                  lg={1.5}
                  sx={{ mt: '25px' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isTotalizer}
                        onChange={(e) => {
                          handleTotalizerChange(e)
                        }}
                        name='totalizer'
                        color='primary'
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: isTotalizer ? 'primary.main' : '#505050'
                          }
                        }}
                      />
                    }
                    label='Enable Totalizer'
                  />
                </Grid>
              )}
              {kpiType?.value === 'option1'
                ? (
                <Grid
                  item
                  xs={gridSizes.xs}
                  sm={1.5}
                  md={1.5}
                  lg={1.5}
                  sx={{ mt: '25px' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabled}
                        onChange={(e) => {
                          setEnabled(e.target.checked)
                        }}
                        name='enabled'
                        color='primary'
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: enabled ? 'primary.main' : '#505050'
                          }
                        }}
                      />
                    }
                    label='Enable'
                  />
                </Grid>
                  )
                : null}
            </Grid>
          </CardContent>
        </Card>

        {kpiType !== null
          ? (
          <>
            {kpiType?.value === 'option2'
              ? (
                  props.deviceType.httpStatus ===
                APIConfiguration.SUCCESS_HTTP_STATUS &&
              props.deviceType.deviceType !== null
                    ? (
                <Card
                  sx={{
                    marginTop: '15px',
                    backgroundColor: '#272727',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    color: '#F0F0F0 !important',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible'
                  }}
                >
                  <div style={{ marginLeft: '15px' }}></div>
                  <CardContent>
                    <Grid container spacing={2} alignItems='flex-start'>
                      <Grid
                        item
                        xs={gridSizes.xs}
                        sm={gridSizes.sm}
                        md={gridSizes.md}
                        lg={gridSizes.lg}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '16px',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {CreateCustomKPIStrings.DeviceType}
                          <sup style={{ color: '#B0B0B0' }}> *</sup>
                        </Typography>
                        <GenXDropdown
                          data-testid='kpitype'
                          placeholder='Select Device Type'
                          value={deviceType}
                          options={deviceTypeOptions}
                          onSelect={handleChooseDeviceType}
                        />
                      </Grid>
                      {deviceType?.value != null && showApiFailurePopup
                        ? (
                        <HandleAPIFailures
                          response={props.deviceModel}
                          handleClose={handleClose}
                          title={'Device Models'}
                        />
                          )
                        : (
                        <></>
                          )}
                      {deviceModelOptions.length > 0 && (
                        <Grid
                          item
                          xs={gridSizes.xs}
                          sm={gridSizes.sm}
                          md={gridSizes.md}
                          lg={gridSizes.lg}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Honeywell Sans Web',
                              fontSize: '16px',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {CreateCustomKPIStrings.DeviceModel}
                            <sup style={{ color: '#B0B0B0' }}> *</sup>
                          </Typography>
                          <GenXDropdown
                            data-testid='kpitype'
                            placeholder='Select Device Model'
                            value={deviceModel}
                            options={deviceModelOptions}
                            onSelect={handleChooseDeviceModel}
                          />
                        </Grid>
                      )}
                      <Grid
                        item
                        xs={gridSizes.xs}
                        sm={gridSizes.sm}
                        md={gridSizes.md}
                        lg={gridSizes.lg}
                        sx={{ mt: '-8px' }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                        >
                          {CreateCustomKPIStrings.Unit}
                        </Typography>
                        <TextField
                          id='remarks-textfield'
                          variant='outlined'
                          placeholder='Text'
                          InputLabelProps={{
                            disabled: true
                          }}
                          size='small'
                          className='custom-textfield'
                          value={unit}
                          onChange={(e) => {
                            // Allow internal spaces but prevent leading/trailing spaces
                            const sanitizedValue = sanitizeInput(e.target.value, 30)
                            setUnit(sanitizedValue)
                            setUnitErrorInit(sanitizedValue.length > 0)
                          }}
                          sx={{
                            ...textStyle.root,
                            width: '80%',
                            border:
                              isUnitNotValid() && unitErrorInit
                                ? '1px solid #EE3426'
                                : 'none'
                          }}
                        />
                        {isUnitNotValid() && unitErrorInit && (
                          <FormHelperText
                            sx={{
                              color: '#EE3426',
                              paddingLeft: '10px',
                              whiteSpace: 'nowrap'
                            }}
                            className='errorColor'
                          >
                            {CreateCustomKPIStrings.UnitValidationMessage}
                          </FormHelperText>
                        )}
                      </Grid>
                      {!customKpiState.totalizer && (
                        <Grid
                          item
                          xs={gridSizes.xs}
                          sm={gridSizes.sm}
                          md={gridSizes.md}
                          lg={gridSizes.lg}
                          sx={{ mt: '-8px' }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Honeywell Sans Web',
                              fontSize: '15px',
                              padding: 0.5,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {KPIDetailStrings.DashboardText}
                            <Tooltip
                              title={
                                <span
                                  style={{
                                    minWidth: 1300,
                                    display: 'inline-block',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {KPIDetailStrings.DashboardTextFormat}
                                  <br />
                                  e.g. &quot;0&quot;:
                                  &quot;Initiate&quot;,&quot;2&quot;:
                                  &quot;Standby&quot;, &quot;4&quot;:
                                  &quot;Purge&quot;, &quot;8&quot;: &quot;Pilot
                                  Ignition&quot;
                                </span>
                              }
                              arrow
                              placement='top'
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#F0F0F0',
                                    color: '#303030',
                                    fontSize: '14px',
                                    boxShadow: 2,
                                    borderRadius: '6px',
                                    maxWidth: 450,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }
                                },
                                arrow: {
                                  sx: {
                                    color: '#F0F0F0'
                                  }
                                }
                              }}
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 18,
                                  color: '#B0B0B0',
                                  marginLeft: '4px',
                                  cursor: 'pointer'
                                }}
                              />
                            </Tooltip>
                          </Typography>
                          <TextField
                            id='remarks-textfield'
                            variant='outlined'
                            placeholder='“Value” : “Name”'
                            InputLabelProps={{
                              disabled: true
                            }}
                            size='small'
                            className='custom-textfield'
                            value={dashboardText}
                            onChange={(e) => {
                              setDashboardText(e.target.value)
                              if (e.target.value.length === 0) { setDashboardTextErrorInit(false) } else setDashboardTextErrorInit(true)
                            }}
                            multiline
                            rows={1}
                            sx={{
                              ...textStyle.root,
                              width: '80%',
                              border:
                                isDashboardTextNotValid() &&
                                dashboardTextErrorInit
                                  ? '1px solid #EE3426'
                                  : 'none',
                              '& .MuiInputBase-root': {
                                padding: 0,
                                borderRadius: '4px'
                              },
                              '& .MuiInputBase-input': {
                                color: '#F0F0F0',
                                backgroundColor: '#404040',
                                padding: '8px 12px',
                                borderRadius: '4px'
                              },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: '#404040',
                                    borderWidth: '1px'
                                  },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#404040'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#404040'
                                }
                              }
                            }}
                          />
                          {isDashboardTextNotValid() &&
                            dashboardTextErrorInit && (
                              <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '10px',
                                  whiteSpace: 'nowrap'
                                }}
                                className='errorColor'
                              >
                                {KPIDetailStrings.DashboardTextValidation}
                              </FormHelperText>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
                      )
                    : showApiFailurePopup
                      ? (
                <HandleAPIFailures
                  response={props.deviceType}
                  handleClose={handleClose}
                  title={'Device Types'}
                />
                        )
                      : (
                <></>
                        )
                )
              : (props.siteList.httpStatus ===
                APIConfiguration.SUCCESS_HTTP_STATUS &&
                props.siteList.sites.length > 0) ||
              kpiType?.value === 'option2'
                  ? (
              <Card
                sx={{
                  marginTop: '15px',
                  backgroundColor: '#272727',
                  borderTopLeftRadius: '10px',
                  borderTopRightRadius: '10px',
                  color: '#F0F0F0 !important',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible'
                }}
              >
                <div style={{ marginLeft: '15px' }}></div>
                <CardContent>
                  <Grid container spacing={4} alignItems='flex-start'>
                    <Grid
                      item
                      xs={gridSizes.xs}
                      sm={gridSizes.sm}
                      md={gridSizes.md}
                      lg={gridSizes.lg}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '16px',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {CreateCustomKPIStrings.Site}
                        <sup style={{ color: '#B0B0B0' }}> *</sup>
                      </Typography>
                      <GenXDropdown
                        data-testid='site'
                        placeholder='Select Site'
                        value={site}
                        options={siteOptions}
                        onSelect={handleChooseSite}
                      />
                    </Grid>
                    {site?.value != null && showApiFailurePopup
                      ? (
                      <HandleAPIFailures
                        response={props.assetDeviceList}
                        handleClose={handleClose}
                        title={'Assets'}
                      />
                        )
                      : (
                      <></>
                        )}
                    <Grid
                      item
                      xs={gridSizes.xs}
                      sm={gridSizes.sm}
                      md={gridSizes.md}
                      lg={gridSizes.lg}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '16px',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {CreateCustomKPIStrings.Asset}
                        <sup style={{ color: '#B0B0B0' }}> *</sup>
                      </Typography>
                      <GenXDropdown
                        data-testid='asset'
                        placeholder='Select Asset'
                        value={asset}
                        options={assetOptions}
                        onSelect={handleChooseAsset}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={gridSizes.xs}
                      sm={gridSizes.sm}
                      md={gridSizes.md}
                      lg={gridSizes.lg}
                      sx={{ mt: '-8px' }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '15px',
                          padding: 0.5,
                          display: 'block'
                        }}
                      >
                        {CreateCustomKPIStrings.Unit}
                      </Typography>
                      <TextField
                        id='remarks-textfield'
                        variant='outlined'
                        placeholder='Text'
                        InputLabelProps={{
                          disabled: true
                        }}
                        size='small'
                        className='custom-textfield'
                        value={unit}
                        onChange={(e) => {
                          // Allow internal spaces but prevent leading/trailing spaces
                          const sanitizedValue = sanitizeInput(e.target.value, 30)
                          setUnit(sanitizedValue)
                          setUnitErrorInit(sanitizedValue.length > 0)
                        }}
                        sx={{
                          ...textStyle.root,
                          width: '80%',
                          border:
                            isUnitNotValid() && unitErrorInit
                              ? '1px solid #EE3426'
                              : 'none'
                        }}
                      />
                      {isUnitNotValid() && unitErrorInit && (
                        <FormHelperText
                          sx={{
                            color: '#EE3426',
                            paddingLeft: '10px',
                            whiteSpace: 'nowrap'
                          }}
                          className='errorColor'
                        >
                          {CreateCustomKPIStrings.UnitValidationMessage}
                        </FormHelperText>
                      )}
                    </Grid>
                    {!customKpiState.totalizer && (
                      <>
                        <Grid
                          item
                          xs={gridSizes.xs}
                          sm={gridSizes.sm}
                          md={gridSizes.md}
                          lg={gridSizes.lg}
                          sx={{ mt: '-8px' }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Honeywell Sans Web',
                              fontSize: '15px',
                              padding: 0.5,
                              display: 'block'
                            }}
                          >
                            {CreateCustomKPIStrings.Frequency}
                            <sup style={{ color: '#B0B0B0' }}> *</sup>
                          </Typography>

                          <GenXDropdown
                            data-testid="frequency"
                            options={frequencyOptions}
                            value={frequencyOptions.find(option => option.value === frequency) ?? null}
                            onSelect={(selectedOption: Option) => {
                              setFrequency(selectedOption.value)
                            }}
                            placeholder="Select Frequency"
                            isSearchable={false}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={gridSizes.xs}
                          sm={gridSizes.sm}
                          md={gridSizes.md}
                          lg={gridSizes.lg}
                          sx={{ mt: '-8px' }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Honeywell Sans Web',
                              fontSize: '15px',
                              padding: 0.5,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {KPIDetailStrings.DashboardText}
                            <Tooltip
                              title={
                                <span
                                  style={{
                                    minWidth: 1300,
                                    display: 'inline-block',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {KPIDetailStrings.DashboardTextFormat}
                                  <br />
                                  e.g. &quot;0&quot;:
                                  &quot;Initiate&quot;,&quot;2&quot;:
                                  &quot;Standby&quot;, &quot;4&quot;:
                                  &quot;Purge&quot;, &quot;8&quot;: &quot;Pilot
                                  Ignition&quot;
                                </span>
                              }
                              arrow
                              placement='top'
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#F0F0F0',
                                    color: '#303030',
                                    fontSize: '14px',
                                    boxShadow: 2,
                                    borderRadius: '6px',
                                    maxWidth: 450,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }
                                },
                                arrow: {
                                  sx: {
                                    color: '#F0F0F0'
                                  }
                                }
                              }}
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 18,
                                  color: '#B0B0B0',
                                  marginLeft: '4px',
                                  cursor: 'pointer'
                                }}
                              />
                            </Tooltip>
                          </Typography>
                          <TextField
                            id='remarks-textfield'
                            variant='outlined'
                            placeholder='“Value” : “Name”'
                            InputLabelProps={{
                              disabled: true
                            }}
                            size='small'
                            className='custom-textfield'
                            value={dashboardText}
                            onChange={(e) => {
                              setDashboardText(e.target.value)
                              if (e.target.value.length === 0) { setDashboardTextErrorInit(false) } else setDashboardTextErrorInit(true)
                            }}
                            multiline
                            rows={1}
                            sx={{
                              ...textStyle.root,
                              width: '80%',
                              border:
                                isDashboardTextNotValid() &&
                                dashboardTextErrorInit
                                  ? '1px solid #EE3426'
                                  : 'none',
                              '& .MuiInputBase-root': {
                                padding: 0,
                                borderRadius: '4px'
                              },
                              '& .MuiInputBase-input': {
                                color: '#F0F0F0',
                                backgroundColor: '#404040',
                                padding: '8px 12px',
                                borderRadius: '4px'
                              },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: '#404040',
                                    borderWidth: '1px'
                                  },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#404040'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#404040'
                                }
                              }
                            }}
                          />
                          {isDashboardTextNotValid() &&
                            dashboardTextErrorInit && (
                              <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '10px',
                                  whiteSpace: 'nowrap'
                                }}
                                className='errorColor'
                              >
                                {KPIDetailStrings.DashboardTextValidation}
                              </FormHelperText>
                          )}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
                    )
                  : showApiFailurePopup
                    ? (
              <HandleAPIFailures
                response={props.siteList}
                handleClose={handleClose}
                title={'Sites'}
              />
                      )
                    : (
              <></>
                      )}
            {(kpiType?.value === 'option1' &&
              site?.value != null &&
              asset?.value != null) ||
            (kpiType?.value === 'option2' &&
              deviceType?.value != null &&
              (deviceType?.label === '7800'
                ? deviceModel?.value != null
                : true))
              ? (
              <Card
                sx={{
                  marginTop: '2px',
                  borderBottomLeftRadius: '10px',
                  borderBottomRightRadius: '10px',
                  borderRadius: '0px',
                  backgroundColor: '#272727',
                  color: '#F0F0F0 !important',
                  padding: '16px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  overflowY: 'auto',
                  flex: 1
                }}
              >
                <CreateKpiTab
                  selectedAsset={asset}
                  assetList={props.assetDeviceList.devices}
                  kpiType={kpiType.value}
                  deviceTypeId={deviceTypeId}
                />
              </Card>
                )
              : (
              <Card
                sx={{
                  marginTop: '15px',
                  borderRadius: '10px',
                  backgroundColor: '#272727',
                  color: '#F0F0F0 !important',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '5em',
                    marginBottom: '5em'
                  }}
                >
                  <CautionWarning
                    heading={headingMessage()}
                    messages={warningMessages()}
                  />
                </div>
              </Card>
                )}
            {operationPermissions.permissions.can_modify_tiq_configurations
              ? (
                  kpiType?.value === 'option1' &&
              enabled && (
                <Card
                  sx={{
                    marginTop: '15px',
                    borderRadius: '10px',
                    backgroundColor: '#272727',
                    color: '#F0F0F0 !important',
                    padding: '10px 20px 30px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Honeywell Sans Web',
                      fontSize: '16px',
                      fontWeight: 800,
                      letterSpacing: '2.5px'
                    }}
                  >
                    {CreateCustomKPIStrings.CONFIGURE_ALARM}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Honeywell Sans Web',
                      color: '#B0B0B0',
                      fontSize: '16px',
                      fontWeight: 500
                    }}
                  >
                    {CreateCustomKPIStrings.Alarm_Note}
                  </Typography>
                  <Grid container spacing={2} alignItems='flex-start'>
                    <Grid
                      item
                      xs={6}
                      sm={6}
                      md={6}
                      lg={6}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          marginRight: '12px',
                          fontSize: '16px',
                          fontWeight: 500
                        }}
                      >
                        {CreateCustomKPIStrings.EnableAlarm}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '15px',
                          padding: 0.5,
                          display: 'block'
                        }}
                      >
                        <FormControlLabel
                          control={
                            <CustomSwitch
                              sx={{ m: 1 }}
                              onChange={() => {
                                setIsAlarm(!isAlarm)
                              }}
                              checked={isAlarm}
                              size='medium'
                              data-testid='switch-alarm'
                            />
                          }
                          style={{ marginTop: '8px' }}
                          label=''
                        />
                      </Typography>
                      {isAlarm && (
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            marginLeft: '-4px',
                            fontSize: '16px'
                          }}
                        >
                          <ConfigureDevice
                            isEdit={false}
                            isAlarmEnabledCompo={false}
                            deviceIndicators={assetIndicators[0]}
                            handleChangeTextBox={handleChangeTextBox}
                            alarmSelect={handleAlarmSelect}
                            handleCancel={handleCancel}
                          />
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  {isAlarm && (
                    <div className='error-response'>
                      <span className='error-upload-message'>
                        Please click on “Configure Alarm” to add alarms.
                      </span>
                    </div>
                  )}
                </Card>
                  )
                )
              : (
              <></>
                )}
          </>
            )
          : (
          <Card
            sx={{
              marginTop: '15px',
              borderRadius: '10px',
              backgroundColor: '#272727',
              color: '#F0F0F0 !important',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible'
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '5em',
                marginBottom: '5em'
              }}
            >
              <CautionWarning
                heading={headingMessage()}
                messages={warningMessages()}
              />
            </div>
          </Card>
            )}
      </>
    )
  }

  const handleCreateKpiWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (
      (!kpiCreated &&
        props.createCustomKpi.httpStatus ===
          APIConfiguration.CREATED_HTTP_STATUS) ||
      props.editCustomKpi.httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS
    ) {
      return (
        <Modal
          buttonTypeNegative='hollow'
          title='Successfully Added'
          LabelPositive='Okay'
          onClickPositive={() => {
            navigate('/custom-kpi-list')
          }}
          onClose={() => {
            navigate('/custom-kpi-list')
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Honeywell Sans Web',
              fontSize: '15px',
              paddingTop: '1.2em'
            }}
          >
            {CreateCustomKPIStrings.CreateSuccessMessage}
          </Typography>
        </Modal>
      )
    } else if (
      (!kpiCreated && props.createCustomKpi.isLoading) ||
      props.editCustomKpi.isLoading
    ) {
      return (
        <Modal title='Creating Custom KPI'>
          <div className='CircularprogressAmi'>
            <CircularProgress />
          </div>
        </Modal>
      )
    } else if (
      (!kpiCreated &&
        props.createCustomKpi.httpStatus ===
          APIConfiguration.UNAUTHORIZED_HTTP_STATUS) ||
      props.editCustomKpi.httpStatus ===
        APIConfiguration.UNAUTHORIZED_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanState}
          onClose={closeAndCleanState}
          title='Creating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='Unauthorized'
            />
          </div>
        </Modal>
      )
    } else if (
      (!kpiCreated &&
        props.createCustomKpi.httpStatus ===
          APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS) ||
      props.editCustomKpi.httpStatus ===
        APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanState}
          onClose={closeAndCleanState}
          title='Creating Custom KPI'
          onClickPositive={() => {
            createCustomKpi()
          }}
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='accessForbidden'
            />
          </div>
        </Modal>
      )
    } else {
      return (
        <Modal
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanState}
          onClose={closeAndCleanState}
          title='Creating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              customError={props.createCustomKpi.error}
            />
          </div>
        </Modal>
      )
    }
  }
  const handleCreateAlarmWithFailure = (): JSX.Element => {
    if (
      !props.assetKpiAlarmState.isLoading &&
      props.assetKpiAlarmState.httpStatus ===
        APIConfiguration.CREATED_HTTP_STATUS
    ) {
      return (
        <Modal
          buttonTypeNegative='hollow'
          title='Successfully Added'
          LabelPositive='Okay'
          onClickPositive={() => {
            navigate('/custom-kpi-list')
          }}
          onClose={() => {
            navigate('/custom-kpi-list')
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Honeywell Sans Web',
              fontSize: '15px',
              paddingTop: '1.2em'
            }}
          >
            {CreateCustomKPIStrings.CreateSuccessMessage}
          </Typography>
        </Modal>
      )
    } else if (props.assetKpiAlarmState.isLoading) {
      return (
        <Modal title='Creating Custom KPI'>
          <div className='CircularprogressAmi'>
            <CircularProgress />
          </div>
        </Modal>
      )
    } else if (
      props.assetKpiAlarmState.httpStatus ===
      APIConfiguration.UNAUTHORIZED_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanAlarmState}
          onClose={closeAndCleanAlarmState}
          title='Creating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='Unauthorized'
            />
          </div>
        </Modal>
      )
    } else if (
      props.assetKpiAlarmState.httpStatus ===
      APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanAlarmState}
          onClose={closeAndCleanAlarmState}
          title='Creating Custom KPI'
          onClickPositive={() => {
            createCustomKpi()
          }}
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='accessForbidden'
            />
          </div>
        </Modal>
      )
    } else {
      return (
        <Modal
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanAlarmState}
          onClose={closeAndCleanAlarmState}
          title='Creating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              customError={props.assetKpiAlarmState.error}
            />
          </div>
        </Modal>
      )
    }
  }

  const handleAlarmSelect = (
    id: string | number,
    propertyName: string,
    value: string
  ): void => {
    const updatedIndicators = assetIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, [propertyName]: value }
        : indicator
    )
    setAssetIndicators([...updatedIndicators])
  }
  const handleChangeTextBox = (
    id: string | number,
    propertyName: string,
    value: string | number
  ): void => {
    const parsedValue: number | string = value
    if (
      propertyName === 'alarmLowerLimit' ||
      propertyName === 'alarmHigherLimit'
    ) {
      if (typeof value === 'number') {
        const minValue = -2147483648
        const maxValue = 2147483647
        if (value < minValue || value > maxValue) {
          setError(true)
          CustomToast({
            type: 'error',
            heading: CreateCustomKPIStrings.Error,
            message: `${String(
              CreateCustomKPIStrings.ValueMustBeBetween
            )} ${minValue} and ${maxValue}`
          })
        }
      }
    }
    setError(false)
    const updatedIndicators = assetIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, [propertyName]: parsedValue }
        : indicator
    )
    setAssetIndicators([...updatedIndicators])
  }
  const handleCancel = (id: string | number): void => {
    const emptyObj = {
      isLowerLimitAlarmEnabled: false,
      lowerLimitOperator: null,
      isHigherLimitAlarmEnabled: false,
      higherLimitOperator: null,
      alarmHigherLimit: null,
      alarmHigherLimitStateText: null,
      alarmHigherLimitRecommendationText: null,
      alarmHigherLimitLabelId: null,
      alarmHigherLimitSeverityId: null,
      alarmHigherLimitTextColorId: null,
      alarmLowerLimit: null,
      alarmLowerLimitStateText: null,
      alarmLowerLimitRecommendationText: null,
      alarmLowerLimitLabelId: null,
      alarmLowerLimitSeverityId: null,
      alarmLowerLimitTextColorId: null
    }
    const updatedIndicators = assetIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, ...emptyObj }
        : indicator
    )
    setAssetIndicators([...updatedIndicators])
  }

  return (
    <div>
      <div
        className='link-breadcrumb'
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <div
          style={{
            marginTop: '6px',
            marginRight: '10px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <BackButton />
        </div>
        <Typography
          data-testid='configurationbreadcrumb'
          onClick={() => {
            navigate('/custom-kpi-list')
          }}
          sx={breadcrumbStyle}
        >
          {CreateCustomKPIStrings.KPIConfiguration}
        </Typography>
        <span style={{ padding: '0 0.5em' }}>/</span>
        <Typography sx={breadcrumbStyle}>
          {CreateCustomKPIStrings.CreateCustomKpi}
        </Typography>
      </div>
      <div className='topUtilityBar'>
        <Typography
          sx={{
            fontFamily: 'Honeywell Sans Web',
            fontSize: '24px',
            fontWeight: 700
          }}
        >
          {CreateCustomKPIStrings.CreateCustomKpi}
        </Typography>
      </div>
      {handleCreationWithFailure()}
      <div
        style={{
          width: '100%',
          height: '80px',
          backgroundColor: '#272727',
          margin: '50px 0 10px 0',
          borderRadius: 10
        }}
      >
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 1em'
          }}
        >
          <Button
            dataTestId='ConfigCancel'
            type='hollow'
            onClick={() => {
              navigate('/custom-kpi-list')
            }}
          >
            {CreateCustomKPIStrings.Cancel}
          </Button>
          <Button
            dataTestId='ConfigCreate'
            primaryColor={checkForIncompleteForm() ? '#B0B0B0' : undefined}
            onClick={() => {
              setShowConfirmation(true)
            }}
            disabled={checkForIncompleteForm()}
          >
            {CreateCustomKPIStrings.Create}
          </Button>
        </div>
      </div>
      {showConfirmation && (
        <Modal
          buttonTypeNegative='hollow'
          title='Create Custom KPI'
          LabelNegative='Cancel'
          LabelPositive='Create'
          onClickPositive={() => {
            setShowConfirmation(false)
            setShowCreatePopup(true)
            createCustomKpi()
          }}
          onClose={onCustomKpiCreationConfirmClose}
          onClickNegative={onCustomKpiCreationConfirmClose}
        >
          <Typography
            sx={{
              fontFamily: 'Honeywell Sans Web',
              fontSize: '15px',
              paddingTop: '1.2em'
            }}
          >
            {CreateCustomKPIStrings.CreateConfirmationMessage}
          </Typography>
        </Modal>
      )}
      {showCreatePopup && handleCreateKpiWithFailure()}
      {showAlarmPopup && handleCreateAlarmWithFailure()}
    </div>
  )
}

interface DispatchToProps {
  fetchAssetDeviceList: (siteId: string, assetId?: string) => void
  createCustomKpiAction: (
    createCustomKpiRequest: CreateCustomKpiRequest
  ) => void
  fetchAssetCategories: () => void
  fetchDeviceType: (categoryTypeId: string) => void
  fetchDeviceModel: (deviceTypeId: string) => void
  fetchSiteList: () => void
  addAssetKpiAlarm: (addAlarmRequest: any, assetId: string) => void
  editCustomKpiDetails: (editKpiRequest: any, kpiDefinitionId: string) => void
  getOperationPermissions: (sub: string) => void
  fetchFrequencyMetaData: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchAssetDeviceList: (siteId: string, assetId?: string) =>
    dispatch(fetchAssetDeviceList(siteId, assetId)),
  createCustomKpiAction: (createCustomKpiRequest: CreateCustomKpiRequest) =>
    dispatch(createCustomKpiAction(createCustomKpiRequest)),
  fetchAssetCategories: () => dispatch(fetchAssetCategories()),
  fetchDeviceType: (categoryTypeId: string) =>
    dispatch(fetchDeviceType(categoryTypeId)),
  fetchDeviceModel: (deviceTypeId: string) =>
    dispatch(fetchDeviceModel(deviceTypeId)),
  fetchSiteList: () => dispatch(fetchSiteList()),
  addAssetKpiAlarm: (addAlarmRequest: any, assetId: string) =>
    dispatch(addAssetKpiAlarm(addAlarmRequest, assetId)),
  editCustomKpiDetails: (editKpiRequest: any, kpiDefinitionId: string) =>
    dispatch(editCustomKpiDetails(editKpiRequest, kpiDefinitionId)),
  getOperationPermissions: (sub: string) =>
    dispatch(getOperationPermissions(sub)),
  fetchFrequencyMetaData: () => dispatch(fetchFrequencyMetaData())
})

interface StateToProps {
  assetDeviceList: AssetDeviceState
  createCustomKpi: CreateCustomKpiState
  assetCategories: AssetCategoriesState
  deviceType: DeviceTypeState
  deviceModel: DeviceModelState
  siteList: SiteListState
  assetKpiAlarmState: AssetAlarmState
  editCustomKpi: EditKpiState
  frequencyMetaDataState: FrequencyMetaDataState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  assetDeviceList: state.tiqAssetList,
  createCustomKpi: state.createCustomKpi,
  assetCategories: state.assetCategories,
  deviceType: state.deviceType,
  deviceModel: state.deviceModel,
  siteList: state.tiqSiteList,
  assetKpiAlarmState: state.assetKpiAlarmData,
  editCustomKpi: state.editCustomKpi,
  frequencyMetaDataState: state.frequencyMetaDataState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(CreateCustomKpi)
